import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Block } from "@/types/proposals";

interface SmartFieldBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  isTemplate?: boolean;
  smartFieldData?: Record<string, string>;
}

export function SmartFieldBlock({ block, onUpdate, isTemplate, smartFieldData }: SmartFieldBlockProps) {
  const variableKey = block.settings?.variableKey || 'customer_name';
  const defaultValue = block.settings?.defaultValue || '';
  const format = block.settings?.format || 'text';
  const blockSize = block.settings?.blockSize || 'medium';
  const textAlign = block.style?.textAlign || 'left';
  
  const value = isTemplate 
    ? `{{${variableKey}}}`
    : (smartFieldData?.[variableKey] || defaultValue || `{{${variableKey}}}`);

  const getTextClass = () => {
    const sizeClass = {
      small: 'text-sm',
      medium: 'text-base',
      large: 'text-xl',
    }[blockSize];

    const alignClass = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    }[textAlign];

    let formatClass = '';
    if (format === 'bold') formatClass = 'font-bold';
    if (format === 'colored') formatClass = 'text-primary font-semibold';

    return `${sizeClass} ${alignClass} ${formatClass}`;
  };

  // In preview/template mode, render ONLY the value text - no card, no labels
  if (isTemplate) {
    return (
      <p 
        className={getTextClass()}
      >
        {value}
      </p>
    );
  }

  // In editor mode, show the full UI with card and input
  return (
    <Card className="p-4 bg-muted/20">
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground capitalize">
          {variableKey.replace(/_/g, ' ')}
        </Label>
        <Input
          value={value}
          onChange={(e) => onUpdate({
            content: {
              ...block.content,
              overrideValue: e.target.value
            }
          })}
          className={getTextClass()}
        />
      </div>
    </Card>
  );
}
